import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Ticket } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 15 * 60 * 1000;
const LIVE_POLL_MS = 60 * 1000;

const SHORT = {
  'Heart of Midlothian': 'Hearts',
  'St Johnstone': 'St Johnstone',
  'Dundee United': 'Dundee Utd',
  'Greenock Morton': 'Morton',
};
const short = (name) => SHORT[name] || name;

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const kickoff = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const time = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/** How long until kick-off, in the way someone would actually say it. */
const untilKickoff = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const minutes = Math.round((d - Date.now()) / 60000);
  if (minutes <= 0) return 'Kick-off now';
  if (minutes < 60) return `Kick-off in ${minutes} min`;
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return `Today, ${time(iso)}`;
  const tomorrow = new Date(today.getTime() + 86400000);
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow, ${time(iso)}`;
  const days = Math.ceil(minutes / 1440);
  if (days <= 14) return `In ${days} days`;
  const weeks = Math.round(days / 7);
  return `In ${weeks} weeks`;
};

// St Mirren sell through their own Future Ticketing portal; away allocations
// go through the same place, so one link covers both.
const TICKETS_URL = 'https://www.stmirren.com/matchtickets';

const FORM_TONE = { W: 'bg-emerald-500', D: 'bg-slate-400', L: 'bg-rose-500' };

const STATUS_LABEL = { HT: 'Half time', FT: 'Full time', AET: 'After extra time', PEN: 'On penalties' };

/**
 * MatchDayStrip — takes over the strip while Saints are playing, showing the
 * score as it moves, then settles on full time before handing back to the
 * ordinary last-result strip.
 */
const MatchDayStrip = ({ match }) => {
  const live = match.live;
  const label = STATUS_LABEL[match.status]
    || (live ? (match.elapsed ? `${match.elapsed}'` : 'In play') : 'Full time');
  const scorers = (match.goals || []).filter((g) => g.player);

  return (
    <section
      className={`border-y ${live ? 'bg-rose-950 border-rose-500/30' : 'bg-slate-900 border-white/10'}`}
      data-testid="st-mirren-matchday-strip"
    >
      <Link to="/st-mirren" className="group block max-w-7xl mx-auto px-4 sm:px-6 py-3" data-testid="st-mirren-matchday-link">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <span
            className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold
                        uppercase tracking-wider shrink-0
                        ${live ? 'bg-rose-500 text-white' : 'bg-white/10 text-slate-300'}`}
            data-testid="st-mirren-live-badge"
          >
            {live && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
            {live ? 'Live' : 'Result'}
          </span>

          <span className="flex items-center gap-2 text-white font-semibold" data-testid="st-mirren-live-score">
            {short(match.home.name)}
            <strong className="text-lg tabular-nums leading-none">
              {match.home_score ?? 0}&ndash;{match.away_score ?? 0}
            </strong>
            {short(match.away.name)}
            <span className={`text-xs font-medium ${live ? 'text-rose-200' : 'text-slate-400'}`}>{label}</span>
          </span>

          {scorers.length > 0 && (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-300"
                  data-testid="st-mirren-live-scorers">
              {scorers.map((g, i) => (
                <span key={i} className={g.is_st_mirren ? 'text-white' : ''}>
                  {g.player}
                  <span className="text-slate-500"> {g.minute}&prime;{g.own_goal ? ' (og)' : ''}{g.penalty ? ' (pen)' : ''}</span>
                </span>
              ))}
            </span>
          )}

          <span className="ml-auto hidden sm:flex items-center gap-1 text-[#e5b611] font-medium
                           group-hover:gap-2 transition-all">
            Saints page <ArrowRight size={13} />
          </span>
        </div>
      </Link>
    </section>
  );
};

/**
 * StMirrenStrip — the Saints' latest score, next fixture and league position,
 * sitting with the other homepage strips so it is seen before any scrolling.
 * Hides itself entirely if no football data is available.
 */
const StMirrenStrip = () => {
  const [data, setData] = useState(null);
  const [match, setMatch] = useState(null);
  const [, setNow] = useState(Date.now());

  useEffect(() => {
    let alive = true;
    const load = () => fetch(`${API}/st-mirren`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setData(d); })
      .catch(() => {});
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Match day: poll the cheap live endpoint so the score moves on its own.
  useEffect(() => {
    let alive = true;
    const load = () => fetch(`${API}/st-mirren/live`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setMatch(d?.match || null); })
      .catch(() => {});
    load();
    const id = setInterval(load, LIVE_POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Ticks the countdown along without re-fetching anything.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const last = data?.results?.[0];
  const next = data?.upcoming?.[0];
  const us = (data?.table || []).find((t) => t.is_st_mirren);
  if (!match && !last && !next && !us) return null;

  if (match) return <MatchDayStrip match={match} />;

  const until = next ? untilKickoff(next.date) : '';

  return (
    <section className="bg-slate-900 border-y border-white/10" data-testid="st-mirren-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
        <Link
          to="/st-mirren"
          className="group flex flex-wrap items-center gap-x-5 gap-y-2 text-sm flex-1 min-w-0"
          data-testid="st-mirren-strip-link"
        >
          <span className="flex items-center gap-2 font-semibold tracking-tight text-white shrink-0">
            <Shield size={15} className="text-[#e5b611]" />
            St Mirren
          </span>

          {last && (
            <span className="flex items-center gap-2 text-slate-200" data-testid="st-mirren-last-result">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Last</span>
              {short(last.home.name)}
              <strong className="text-white tabular-nums">
                {last.home_score}&ndash;{last.away_score}
              </strong>
              {short(last.away.name)}
            </span>
          )}

          {next && (
            <span className="flex items-center gap-2 text-slate-200" data-testid="st-mirren-next-fixture">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Next</span>
              {next.is_home ? short(next.opponent) : `at ${short(next.opponent)}`}
              <span className="text-slate-400">{kickoff(next.date)}</span>
              {until && (
                <span className="text-[#e5b611] font-medium" data-testid="st-mirren-countdown">
                  {until}
                </span>
              )}
            </span>
          )}

          {us?.rank && (
            <span className="flex items-center gap-2 text-slate-200" data-testid="st-mirren-position">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Table</span>
              {ordinal(us.rank)}
              <span className="text-slate-400">{us.points} pts</span>
            </span>
          )}

          {data?.form?.length > 0 && (
            <span className="flex items-center gap-1" data-testid="st-mirren-form">
              {data.form.slice(0, 5).map((f, i) => (
                <span
                  key={i}
                  className={`w-4 h-4 rounded-sm ${FORM_TONE[f] || 'bg-slate-600'} text-white
                              text-[9px] font-bold flex items-center justify-center`}
                  title={`${f === 'W' ? 'Win' : f === 'L' ? 'Loss' : 'Draw'}`}
                >
                  {f}
                </span>
              ))}
            </span>
          )}
        </Link>

        {next && (
          <a
            href={TICKETS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#e5b611]
                       text-slate-900 text-xs font-semibold hover:bg-[#f0c72a]
                       transition-colors"
            title={`Tickets for ${next.is_home ? 'the home game against' : 'the away game at'} ${next.opponent}, via St Mirren FC`}
            data-testid="st-mirren-tickets-link"
          >
            <Ticket size={13} />
            Tickets
          </a>
        )}

        <Link
          to="/st-mirren"
          className="group shrink-0 hidden sm:flex items-center gap-1 text-[#e5b611] text-sm
                     font-medium hover:gap-2 transition-all"
          data-testid="st-mirren-page-link"
        >
          Saints page <ArrowRight size={13} />
        </Link>
      </div>
    </section>
  );
};

export default StMirrenStrip;
